<template>
  <CommonLoading v-if="loading" />
  <AtendimentosWhatsAppTemplateForm
    :open="templateOpen"
    :templates="templatesWhats"
    @update-open= "templateOpen = false"
    @enviar-template = "enviarTemplate"
  />

  <div class="flex h-screen w-full bg-gray-100">
    <!-- LISTA DE NÚMEROS (Números não identificados globais do sistema) -->
    <div class="w-80 lg:w-96 border-r border-gray-200 bg-white flex flex-col flex-shrink-0">

      <!-- Header -->
      <div class="p-4 border-b border-gray-200">
        <h1 class="text-xl font-semibold mb-4 text-gray-800">Números Desconhecidos</h1>

        <!-- Barra de busca -->
        <div class="flex gap-2 mb-4">
          <div class="relative flex-1">
            <input
              type="text"
              placeholder="Buscar telefone na triagem..."
              class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
              v-model="filters.cliente"
              @keyup.enter="buscarCliente"
            />
          </div>
          <button
            type="button"
            class="bg-indigo-600 text-white text-sm font-medium px-4 rounded-md hover:bg-indigo-700 transition-colors"
            @click="buscarCliente"
          >
            Buscar
          </button>
        </div>
      </div>

      <!-- Lista -->
      <div class="flex-1 overflow-y-auto">
        <div
          v-for="p in pessoas"
          :key="p.id"
          @click="abrirChatEBusca(p)"
          class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors"
          :class="{'bg-indigo-50 border-l-4 border-indigo-500': contatoSelecionado?.id === p.id}"
        >
          <div class="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500">
            ?
          </div>

          <div class="flex flex-col overflow-hidden">
            <span class="font-semibold text-gray-900 truncate">{{ p.telefone}}</span>
            <span class="text-xs text-gray-500 truncate" v-if="p.ultimaMensagem">{{ p.ultimaMensagem }}</span>
            <span class="text-xs text-gray-400 italic" v-else>Aguardando mensagens...</span>
          </div>
        </div>

        <div v-if="pessoas.length === 0" class="p-8 text-center flex flex-col items-center justify-center h-full">
            <svg class="h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
           <span class="text-gray-500 text-sm">Nenhum número desconhecido na rede.</span>
        </div>
      </div>

      <!-- Paginação Lista Central -->
      <div class="p-3 border-t border-gray-200 bg-gray-50">
         <div class="flex justify-between items-center text-xs">
          <span class="text-gray-600 font-medium">Pág {{ currentPageLista }} de {{ totalPagesLista || 1 }}</span>
          <div class="flex space-x-1">
            <button @click="previousPageListas" :disabled="!listasWhatsPrevPageUrl" class="px-3 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors">Anterior</button>
            <button @click="nextPageListas" :disabled="!listasWhatsNextPageUrl" class="px-3 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors">Próximo</button>
          </div>
        </div>
      </div>

    </div>

    <!-- MAIN CONTENT: CHAT & SEARCH -->
    <div class="flex-1 flex" v-if="contatoSelecionado">
      
      <!-- COLUNA CENTRAL: CHAT -->
      <div class="flex-1 flex flex-col bg-[#e5ddd5] relative">
        
        <!-- Topo do Chat -->
        <div class="bg-gray-100 p-4 border-b border-gray-200 flex items-center justify-between shadow-sm z-10">
          <div class="flex items-center gap-3">
             <div class="h-10 w-10 flex-shrink-0 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-600">
              ?
             </div>
             <span class="font-bold text-lg text-gray-800">{{ contatoSelecionado.telefone }}</span>
          </div>
        </div>

        <!-- Mensagens do Chat -->
        <div class="flex-1 overflow-y-auto p-4 space-y-3" id="mensagens-container">
          <div
            v-for="m in mensagens"
            :key="m.id"
            :class="m.isCliente ? 'text-left' : 'text-right'"
          >
            <span
              class="inline-block px-4 py-2 rounded-lg max-w-[80%] shadow-sm"
              :class="m.isCliente
                ? 'bg-white text-gray-800 rounded-tl-none'
                : 'bg-[#dcf8c6] text-gray-800 rounded-tr-none'"
            >
              {{ m.texto }}
            </span>
          </div>
          <div v-if="mensagens.length === 0" class="text-center text-gray-500 bg-white/50 py-2 px-4 rounded-full mx-auto w-max text-sm shadow-sm mt-10">
            Nenhuma mensagem ainda. Inicie a conversa para identificar o cliente.
          </div>
        </div>

        <!-- Input de Envio de Mensagem (Liberado) -->
        <div class="p-3 border-t border-gray-300 bg-gray-100" v-if="podeEnviarMensagemNormal">
          <form @submit.prevent="onSendFormData" class="flex gap-2">
            <input
              v-model="novaMensagem"
              type="text"
              placeholder="Digite sua mensagem para o número desconhecido..."
              class="flex-1 border-none rounded-full px-5 py-3 focus:ring-2 focus:ring-indigo-500 shadow-sm text-sm"
              :disabled="loading"
            />
            <button
              type="submit"
              :disabled="loading || !novaMensagem.trim()"
              class="px-5 py-3 rounded-full bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center justify-center transition-colors"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </form>
        </div>

        <!-- Botão de Envio de Template (Bloqueado pela Meta) -->
        <div class="p-4 border-t border-gray-300 bg-gray-100 flex justify-center" v-else>
            <button
              @click="templateOpen = true"
              class="w-full max-w-sm px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 shadow-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Enviar Template (Janela 24h Expirada)
            </button>
        </div>
      </div>

      <!-- COLUNA DIREITA: OPÇÕES DE VÍNCULO -->
      <div class="w-80 lg:w-[400px] bg-white border-l border-gray-200 flex flex-col flex-shrink-0 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.05)] z-20">

        <!-- Topo da Pesquisa -->
        <div class="bg-indigo-600 p-6 flex flex-col items-center justify-center text-center">
          <h2 class="text-white font-bold text-xl uppercase tracking-wider mb-1">Identificar Cliente</h2>
          <span class="text-indigo-100 text-sm">Pesquise para vincular este contato.</span>
        </div>

      <!-- Área de Pesquisa -->
      <div class="p-6 bg-white border-b border-gray-200">
        <h3 class="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
            <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Buscar Cliente Existente
        </h3>
        
        <form @submit.prevent="pesquisarSegurado" class="flex gap-3">
          <select v-model="tipoBuscaSegurado" class="border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 text-gray-700 outline-none transition-shadow">
            <option value="nome">Nome</option>
            <option value="cpf">CPF</option>
          </select>

          <input
            v-model="termoBuscaSegurado"
            type="text"
            :placeholder="tipoBuscaSegurado === 'cpf' ? 'Digite o CPF...' : 'Digite o Nome do cliente...'"
            class="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-shadow"
          />

          <button
            type="submit"
            :disabled="loading || !termoBuscaSegurado"
            class="px-6 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            Buscar
          </button>
        </form>
      </div>

      <!-- Resultados da Pesquisa de Segurado -->
      <div class="flex-1 overflow-y-auto p-6">
        
        <div v-if="buscaSeguradoRealizada && seguradosEncontrados.length === 0" class="flex flex-col items-center justify-center p-8 bg-white rounded-xl border border-dashed border-gray-300 h-64">
           <svg class="h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
           </svg>
          <span class="text-gray-600 font-medium text-center">Nenhum cliente encontrado com "{{ termoBuscaSegurado }}".</span>
          <p class="text-sm text-gray-400 mt-1 max-w-sm text-center">Verifique se digitou corretamente ou crie um novo cliente.</p>
          <button class="mt-4 px-4 py-2 bg-green-50 text-green-700 border border-green-200 font-semibold rounded-lg hover:bg-green-100 transition-colors">
            + Criar novo Cliente (Em breve)
          </button>
        </div>

        <div v-else-if="seguradosEncontrados.length > 0" class="space-y-4">
          <div class="flex items-center justify-between mb-2">
              <h4 class="text-sm font-bold text-gray-500 uppercase tracking-wider">Resultados Encontrados ({{ seguradosEncontradosTotal }})</h4>
          </div>
          
          <div
            v-for="segurado in seguradosEncontrados"
            :key="segurado.id"
            class="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group"
          >
            <div class="flex items-start gap-4">
                <div class="h-10 w-10 mt-1 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-700 font-bold text-lg">
                    {{ segurado.nome ? segurado.nome.charAt(0).toUpperCase() : 'C' }}
                </div>
                <div>
                  <p class="font-bold text-gray-900 text-lg">{{ segurado.nome || 'Nome Indisponível' }}</p>
                  <div class="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                    <p class="text-sm text-gray-600 flex items-center gap-1">
                        <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                        CPF: <span class="font-medium font-mono text-gray-800">{{ segurado.cpf || 'Não inf.' }}</span>
                    </p>
                    <p class="text-sm text-gray-600 flex items-center gap-1" v-if="segurado.telefone">
                        <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        Tel: <span class="font-medium font-mono text-gray-800">{{ segurado.telefone }}</span>
                    </p>
                  </div>
                </div>
            </div>
            
            <button
              @click="vincularNumeroAoSegurado(segurado.id)"
              class="px-5 py-2.5 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transform transition-all active:scale-95 disabled:opacity-75 flex items-center gap-2"
              :disabled="loadingVincular === segurado.id"
            >
              <svg v-if="loadingVincular === segurado.id" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span>{{ loadingVincular === segurado.id ? 'Vinculando...' : 'Vincular' }}</span>
            </button>
          </div>

          <!-- Paginação Busca Segurados -->
          <div class="flex justify-center items-center gap-4 mt-8" v-if="seguradosEncontradosTotalPages > 1">
            <button 
              @click="buscarSeguradoPaginaAnterior" 
              :disabled="!seguradosPrevPageUrl"
              class="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              &larr; Anterior
            </button>
            <span class="text-sm font-medium text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-100 shadow-sm">
                Página <span class="text-gray-900">{{ currentSeguradoPage }}</span> de {{ seguradosEncontradosTotalPages }}
            </span>
            <button 
              @click="buscarSeguradoProximaPagina" 
              :disabled="!seguradosNextPageUrl"
              class="px-4 py-2 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próxima &rarr;
            </button>
          </div>

        </div>
        
        <div v-else class="text-center py-20 opacity-60">
          <svg class="mx-auto h-20 w-20 text-gray-300 mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-gray-600 text-lg font-medium">Pronto para buscar!</p>
          <p class="text-gray-400 mt-2 max-w-sm mx-auto">Pesquise pelo Nome ou CPF do cliente para encontrar seu cadastro e vincular este número de WhatsApp.</p>
        </div>

      </div>

      </div>
    </div>
    
    <!-- Placeholder quando não selecionou número -->
    <div v-else class="flex-1 flex flex-col items-center justify-center bg-gray-50 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] bg-opacity-50">
      <div class="bg-white p-10 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center max-w-lg text-center transform hover:scale-105 transition-transform duration-300">
          <div class="h-28 w-28 bg-gradient-to-tr from-indigo-50 to-indigo-100 rounded-full flex items-center justify-center mb-6 text-indigo-600 shadow-inner">
            <svg class="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
             </svg>
          </div>
          <h3 class="text-3xl font-bold text-gray-800 mb-3 tracking-tight">Triagem de Conversas</h3>
          <p class="text-gray-500 text-lg leading-relaxed">
            Selecione um número desconhecido na lista lateral para conversar, identificar a pessoa, e realizar a vinculação cadastral!
          </p>
      </div>
    </div>
  </div>
</template>

<script>
import SeguradosRepository from '@/services/SeguradosRepository.vue';
import { pageVariables as pageVariablesSegurados } from '@/views/insideviews/segurados/PageVariablesSegurados';
import ListaDeAgendamentosRepository from '@/services/ListaDeAgendamentosRepository.vue';
import AtendimentoWhatsappRepository from '@/services/AtendimentoWhatsappRepository.vue';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';
import Swal from "sweetalert2";
import CommonLoading from '@/components/generalcomponents/CommonLoading.vue';
import AtendimentosWhatsAppTemplateForm from './AtendimentosWhatsAppTemplateForm.vue';
import Pusher from 'pusher-js';
import axios from 'axios';

export default {
  name: "AtendimentosWhatsAppTriageView",
  components: {
    CommonLoading,
    AtendimentosWhatsAppTemplateForm
  },
  async created() {
    this.onlineUser = JSON.parse(window.localStorage.getItem("myUser") || "{}");
    this.getLista(); // Buscar as caixas de atendimento
    this.getTemplates();
  },
  data() {
    return {
      onlineUser: {},
      loading: false,
      loadingVincular: null,
      
      // Central List (Unidentified Numbers)
      pessoas: [],
      contatoSelecionado: null,
      filters: { cliente: '' },
      currentPageLista: 0,
      totalPagesLista: 0,
      listasWhatsNextPageUrl: null,
      listasWhatsPrevPageUrl: null,
      listasActualPage: null,

      // Chat Variables
      mensagens: [],
      novaMensagem: "",
      conversaId: null,
      templateOpen: false,
      templatesWhats: [],

      // Right Panel (Segurados Search)
      tipoBuscaSegurado: 'nome',
      termoBuscaSegurado: '',
      buscaSeguradoRealizada: false,
      seguradosEncontrados: [],
      seguradosEncontradosTotal: 0,
      seguradosEncontradosTotalPages: 0,
      currentSeguradoPage: 1,
      seguradosNextPageUrl: null,
      seguradosPrevPageUrl: null,
      
      // WebSocket
      pusher: null,
      canalTriagem: null,
    };
  },
  mounted() {
    this.initBroadcast();
  },
  beforeUnmount() {
      if (this.pusher && this.canalTriagem) {
          this.pusher.unsubscribe('triagem');
      }
  },
  computed: {
    podeEnviarMensagemNormal() {
      if (!this.mensagens?.length) return false;

      // 1) Procura a ÚLTIMA mensagem do cliente
      const ultimaMsgCliente = [...this.mensagens]
          .reverse()
          .find(msg => msg.autor === 'cliente');

      if (!ultimaMsgCliente) return false;

      // 2) Calcula diferença de tempo
      const agora = new Date();
      const dataUltima = new Date(ultimaMsgCliente.created_at);

      const dentroDaJanela24h =
          (agora - dataUltima) < (24 * 60 * 60 * 1000);

      // 3) Retorna verdadeiro se estiver no prazo
      return dentroDaJanela24h;
    }
  },
  methods: {
    // ---------------------------------------------------------
    // 1. GET CHANNELS (Left Sidebar)
    // ---------------------------------------------------------
    async getLista() {
       this.getListaWhatsApp();
    },

    // ---------------------------------------------------------
    // 2. GET UNIDENTIFIED NUMBERS (Central List)
    // ---------------------------------------------------------
    async getListaWhatsApp(pageUrl = null) {

      this.loading = true;
      let finalUrl = pageUrl;

      if (!finalUrl) {
        // Endpoint real backend criado para puxar numeros globais não associados
        finalUrl = `${process.env.VUE_APP_API_URL}whatsapp/numeros-desconhecidos`;
        const params = [];
        if (this.filters.cliente) params.push(`busca=${this.filters.cliente}`);
        if (params.length) finalUrl += '?' + params.join('&');
      }

      this.listasActualPage = finalUrl;
      const listasRepoWhatsRepo = new ListaDeAgendamentosRepository();

      try {
        const response = await listasRepoWhatsRepo.getListaWhatsApp(this.onlineUser.token, finalUrl);
        if (response?.status === 200) {
          this.pessoas = response.data.data.map(item => {
             const ultimaMsg = item.mensagens && item.mensagens.length > 0 ? item.mensagens[0].conteudo : "Aguardando mensagens...";
             return {
               id: item.id,
               telefone: item.cliente_id || '+55 (11) 90000-0000',
               ultimaMensagem: ultimaMsg,
             }
          });

          this.totalPagesLista = response.data.last_page;
          this.currentPageLista = response.data.current_page;
          this.listasWhatsNextPageUrl = response.data.next_page_url;
          this.listasWhatsPrevPageUrl = response.data.prev_page_url;
        }
      } catch (error) {
        handleUnauthorized(error);
      } finally {
        this.loading = false;
      }
    },

    buscarCliente() {
      this.getListaWhatsApp();
    },
    
    previousPageListas(){
      if (this.listasWhatsPrevPageUrl) this.getListaWhatsApp(this.listasWhatsPrevPageUrl);
    },
    
    nextPageListas(){
      if (this.listasWhatsNextPageUrl) this.getListaWhatsApp(this.listasWhatsNextPageUrl);
    },

    abrirChatEBusca(contato) {
      if(this.contatoSelecionado?.id === contato.id) return;
      this.contatoSelecionado = contato;
      // Reset search context
      this.termoBuscaSegurado = '';
      this.buscaSeguradoRealizada = false;
      this.seguradosEncontrados = [];
      
      this.getMensagens(contato.id);
    },

    // ---------------------------------------------------------
    // 3. CHAT LOGIC
    // ---------------------------------------------------------
    async getTemplates(){
      this.loading = true; 
      const whatsRepo = new AtendimentoWhatsappRepository();
      try {
        const response = await whatsRepo.getTemplates(this.onlineUser.token);
        if (response?.status  === 200) { 
          this.templatesWhats = response.data;
        }
      } catch (error) {
        handleUnauthorized(error); 
      } finally {
        this.loading = false; 
      }
    },

    async enviarTemplate(dados) {
      if(!this.contatoSelecionado) return;
      const variables = { ...dados.valores }
      const payload = {
        cliente_id: this.contatoSelecionado.telefone.replace(/\D/g, ''),
        conversa_id: this.conversaId ?? "",
        lista_agendamento_id: "", // Não tem lista na triagem global
        template_name: dados.template_name,
        language: 'pt_BR',
        variables,
        header: {}
      }
      const whatsRepo = new AtendimentoWhatsappRepository();
      this.loading = true;
      try {
        const response = await whatsRepo.enviarTemplate(payload, this.onlineUser.token);
        if (response?.status === 200) {
          const newMessageSent =  response.data?.data;
          if (newMessageSent) {
            const mensagem = {
              id: newMessageSent.id,
              texto: newMessageSent.conteudo,
              autor: newMessageSent.autor,
              isCliente: newMessageSent.autor === 'cliente',
              created_at: newMessageSent.created_at
            };
            this.mensagens.push(mensagem);
            this.rolarParaFinalConversa();
          }
          this.templateOpen = false;
        }
      } catch (error) {
        handleUnauthorized(error); 
      } finally {
        this.loading = false;
      }
    },
    async getMensagens(id) {
			this.loading = true; 
			const whatsRepo = new AtendimentoWhatsappRepository();
			try {
				const response = await whatsRepo.getConversaById(id, this.onlineUser.token);
				if (response?.status  === 200) { 
          const conversa = response.data;
          if (conversa) {
            this.conversaId = conversa.id;
            this.mensagens = (conversa.mensagens ?? []).map(m => {
              return {
                id: m.id,
                texto: m.conteudo,
                autor: m.autor,
                isCliente: m.autor === 'cliente',
                created_at: m.created_at
              };
            });
            this.rolarParaFinalConversa();
          } else {
            this.conversaId = null;
            this.mensagens = [];
          }
				}
			} catch (error) {
				handleUnauthorized(error); 
			} finally {
        this.loading = false; 
      }
		},

    async onSendFormData() {
      if(!this.novaMensagem.trim()) return;
			const whatsRepo = new AtendimentoWhatsappRepository();
      
      const payload = {
        conversa_id: this.conversaId ?? "",
        lista_agendamento_id: "", // null or empty since it's global triage
        mensagem: this.novaMensagem,
        cliente_id: this.contatoSelecionado.telefone.replace(/\D/g, '')
      };

      this.loading = true;
			try {
				const response = await whatsRepo.createMensagem(payload, this.onlineUser.token);
        const newMessageSent =  response.data?.data;
        if(newMessageSent) {
          const mensagem = {
            id: newMessageSent.id,
            texto: newMessageSent.conteudo,
            autor: newMessageSent.autor,
            isCliente: false,
            created_at: newMessageSent.created_at
          };
          this.mensagens.push(mensagem);
          this.rolarParaFinalConversa();
        }
        this.novaMensagem = "";
      } catch (error) {
        handleUnauthorized(error); 
      } finally {
        this.loading = false;
      }
		},

    rolarParaFinalConversa() {
      this.$nextTick(() => {
        const container = document.getElementById('mensagens-container');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    // ---------------------------------------------------------
    // 4. SEGURADOS SEARCH & LINKING (Right Panel)
    // ---------------------------------------------------------
    async pesquisarSegurado() {
      if (!this.termoBuscaSegurado) return;
      this.loading = true;
      this.buscaSeguradoRealizada = true;

      const seguradosRepo = new SeguradosRepository();
      
      const searchPayload = {};
      if (this.tipoBuscaSegurado === 'cpf') {
        searchPayload.cpf = this.termoBuscaSegurado;
      } else {
        searchPayload.nome = this.termoBuscaSegurado;
      }

      try {
        const response = await seguradosRepo.getSegurados(
          this.onlineUser.token,
          pageVariablesSegurados.pageGetSegurados, // URL: /getSegurados
          searchPayload
        );

        if (response?.status === 200) {
          this.seguradosEncontrados = response.data.data;
          this.seguradosEncontradosTotal = response.data.total;
          this.seguradosEncontradosTotalPages = response.data.last_page;
          this.currentSeguradoPage = response.data.current_page;
          this.seguradosNextPageUrl = response.data.next_page_url;
          this.seguradosPrevPageUrl = response.data.prev_page_url;
        }
      } catch (error) {
        console.error("Erro na busca de segurado:", error);
        Swal.fire('Erro na Busca', 'Ocorreu um problema ao buscar clientes. Tente novamente.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async buscarSeguradoPaginaEspecifica(url) {
      if (!url) return;
      this.loading = true;
      const seguradosRepo = new SeguradosRepository();
      
      const searchPayload = {};
      if (this.tipoBuscaSegurado === 'cpf') {
        searchPayload.cpf = this.termoBuscaSegurado;
      } else {
        searchPayload.nome = this.termoBuscaSegurado;
      }

      try {
        const response = await seguradosRepo.getSegurados(this.onlineUser.token, url, searchPayload);
        if (response?.status === 200) {
          this.seguradosEncontrados = response.data.data;
          this.currentSeguradoPage = response.data.current_page;
          this.seguradosNextPageUrl = response.data.next_page_url;
          this.seguradosPrevPageUrl = response.data.prev_page_url;
        }
      } catch (error) {
         console.error("Erro paginação segurados:", error);
      } finally {
        this.loading = false;
      }
    },

    buscarSeguradoProximaPagina() {
      this.buscarSeguradoPaginaEspecifica(this.seguradosNextPageUrl);
    },

    buscarSeguradoPaginaAnterior() {
      this.buscarSeguradoPaginaEspecifica(this.seguradosPrevPageUrl);
    },

    vincularNumeroAoSegurado(seguradoId) {
      Swal.fire({
        title: 'Confirmar Vínculo',
        html: `Confirma vincular o número <b>${this.contatoSelecionado.telefone}</b> a este cliente? <br><br> <span class="text-sm text-gray-500">As mensagens futuras deste número serão associadas automaticamente a ele.</span>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#16a34a', // green-600
        cancelButtonColor: '#6b7280', // gray-500
        confirmButtonText: 'Confirmar Vínculo',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.executarVinculoBackend(seguradoId);
        }
      });
    },

    async executarVinculoBackend(seguradoId) {
      this.loadingVincular = seguradoId;
      
      try {
        const payload = {
          numero: this.contatoSelecionado.telefone,
          segurado_id: seguradoId
        };
        
        await axios.post(`${process.env.VUE_APP_API_URL}whatsapp/vincular-numero`, payload, {
          headers: { Authorization: `Bearer ${this.onlineUser.token}` }
        });
        
        Swal.fire({
            title: 'Vinculado com Sucesso!',
            text: 'O número foi associado ao cliente e as mensagens foram atualizadas.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
        });

        // Remove the linked number from the unindentified list locally
        this.pessoas = this.pessoas.filter(p => p.id !== this.contatoSelecionado.id);
        this.contatoSelecionado = null;

      } catch (error) {
        console.error("Erro no vínculo:", error);
        Swal.fire('Falha no Vínculo', 'Não foi possível salvar o vínculo no sistema.', 'error');
      } finally {
        this.loadingVincular = null;
      }
    },

    // ---------------------------------------------------------
    // 4. WEBSOCKET (Live updates for unknowns)
    // ---------------------------------------------------------
    initBroadcast() {
      try {
        if (!this.onlineUser || !this.onlineUser.user.email) return;
        if (typeof Pusher === "undefined") {
          console.warn("Pusher is undefined in TriageView.");
          return;
        }

        const APP_KEY = process.env.VUE_APP_REVERB_APP_KEY;
        this.pusher = new Pusher(APP_KEY, {
            cluster: "mt1",
            wsHost: "justechv2.sienaseguros.com.br",
            wsPort: 443,
            wssPort: 443,
            forceTLS: true,
            enabledTransports: ['ws', 'wss'],
            disableStats: true
        });

        // Backend will need to emit to this specific channel
        this.canalTriagem = this.pusher.subscribe(`triagem`);

        this.canalTriagem.bind("pusher:subscription_succeeded", () => {
          console.log("🟢 Triage View: Subscribed to 'triagem' channel");
        });
        
        this.canalTriagem.bind("mensagem.desconhecida", (eventData) => {
          console.log("📬 Nova mensagem desconhecida global recebida via socket!", eventData);
          const payloadMensagem = eventData?.mensagem;
          
          if(payloadMensagem && this.contatoSelecionado && this.contatoSelecionado.telefone === payloadMensagem.telefone) {
             const novaMensagem = {
                id: payloadMensagem.id,
                texto: payloadMensagem.conteudo,
                autor: 'cliente',
                isCliente: true,
                created_at: payloadMensagem.created_at
             };
             this.mensagens.push(novaMensagem);
             this.rolarParaFinalConversa();
          }

          // Atualiza a lista da esquerda para trazer pro topo ou adicionar o novo
          this.getListaWhatsApp(this.listasActualPage); 
        });

      } catch (e) {
        console.error("🔴 Pusher init failed in Triage:", e);
      }
    }
  }
};
</script>

<style scoped>
/* Scrollbar custom for aesthetic panel */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}
.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent; 
}
.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1; 
  border-radius: 4px;
}
.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8; 
}
</style>
